/**
 * GL subject-key derivation (GL-1 §1 / GL-P): the MANDATORY `userIdHash` for the
 * event envelope. Events must never carry a raw identifier, so the live mount
 * derives a stable, non-reversible hash of the authed user id (the same `userId`
 * the exam container already reads from localStorage) and passes THAT as the sole
 * subject key.
 *
 * Synchronous by construction: `gl-events.buildGlEvent` runs at the moment of a
 * WHY/hint transition and needs the subject key inline, so we cannot wait on the
 * async WebCrypto digest. A compact, standard SHA-256 is embedded and pinned to
 * the FIPS-180-4 "abc" test vector in gl-identity.test.js, so the hash is both
 * deterministic and verifiably correct without a runtime crypto dependency.
 *
 * The result is a hex SHA-256 of `"glv1:" + userId` (domain-separated so the same
 * id used elsewhere never collides with a GL subject key). Returns null when no
 * user id is present (anonymous) — the event then fails the mandatory-envelope
 * check in buildGlEvent and is not emitted.
 */

/* eslint-disable no-bitwise */
const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
];

const rrot = (x, n) => (x >>> n) | (x << (32 - n));

/** Standard SHA-256 over a UTF-8 string -> 64-char lowercase hex. */
export const sha256Hex = (input) => {
  // UTF-8 encode
  const bytes = [];
  for (let i = 0; i < input.length; i += 1) {
    let c = input.charCodeAt(i);
    if (c < 0x80) {
      bytes.push(c);
    } else if (c < 0x800) {
      bytes.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    } else if (c < 0xd800 || c >= 0xe000) {
      bytes.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    } else {
      // surrogate pair
      i += 1;
      c = 0x10000 + (((c & 0x3ff) << 10) | (input.charCodeAt(i) & 0x3ff));
      bytes.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 0x3f), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
  }

  const l = bytes.length;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) {
    bytes.push(0);
  }
  const bitLen = l * 8;
  // 64-bit big-endian length (high word is 0 for our inputs)
  bytes.push(0, 0, 0, 0);
  bytes.push((bitLen >>> 24) & 0xff, (bitLen >>> 16) & 0xff, (bitLen >>> 8) & 0xff, bitLen & 0xff);

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  const w = new Array(64);
  for (let off = 0; off < bytes.length; off += 64) {
    for (let i = 0; i < 16; i += 1) {
      const j = off + i * 4;
      w[i] = ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) >>> 0;
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = rrot(w[i - 15], 7) ^ rrot(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rrot(w[i - 2], 17) ^ rrot(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }

    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let i = 0; i < 64; i += 1) {
      const S1 = rrot(e, 6) ^ rrot(e, 11) ^ rrot(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rrot(a, 2) ^ rrot(a, 13) ^ rrot(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
  }

  const hex = (x) => ("00000000" + (x >>> 0).toString(16)).slice(-8);
  return hex(h0) + hex(h1) + hex(h2) + hex(h3) + hex(h4) + hex(h5) + hex(h6) + hex(h7);
};
/* eslint-enable no-bitwise */

const cache = { userId: null, hash: null };

/** Stable, domain-separated, non-reversible subject key for a raw user id. */
export const hashUserId = (userId) => {
  if (userId === undefined || userId === null || `${userId}`.length === 0) {
    return null;
  }
  const id = `${userId}`;
  if (cache.userId === id) {
    return cache.hash;
  }
  const hash = sha256Hex(`glv1:${id}`);
  cache.userId = id;
  cache.hash = hash;
  return hash;
};

/** Live subject key from the authed session (localStorage userId). */
export const getUserIdHash = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  return hashUserId(window.localStorage.getItem("userId"));
};
